import {
  type SearchPagination,
  type SearchResponse,
} from '@algolia/client-search';
import { type ReactNode, useCallback, useMemo } from 'react';
import { useLocation, useMatches } from 'react-router';

import { useAppConfig } from '#/config/AppConfigContext';
import { useEnvironment } from '#/contexts/UserEnvironmentContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { I18nLocale } from '#/i18n/types/I18nLocale';
import { isSearchLandingPageSSRMatch } from '#/modules/cms/pages/splat/isSearchLandingPageSSRMatch';
import { useEmbedSearchWidget } from '#/modules/embedSearchWidget/contexts/EmbedSearchWidgetContext';
import { useEmbedSearchWidgetLayoutRouteLoaderData } from '#/modules/embedSearchWidget/hooks/useEmbedSearchWidgetLayoutRouteLoaderData';
import type { ApiVolopPromotionType } from '#/modules/listing/volops/api/types/ApiVolopPromotionType';
import { searchAlgoliaOptionsLocationData } from '#/modules/search/algolia/options/searchAlgoliaOptionsLocationData';
import { searchAlgoliaOptionsWithSearchLocation } from '#/modules/search/algolia/options/searchAlgoliaOptionsWithSearchLocation';
import { searchAlgoliaOptionsSwitch } from '#/modules/search/algolia/options/switch/searchAlgoliaOptionsSwitch';
import { getSearchAlgoliaDefaultConfig } from '#/modules/search/algolia/searchAlgoliaConfig';
import { searchAlgoliaIndexSwitch } from '#/modules/search/algolia/searchAlgoliaIndexSwitch';
import type { SearchAlgoliaHit } from '#/modules/search/algolia/types/SearchAlgoliaHit';
import type { SearchAlgoliaVolopHit } from '#/modules/search/algolia/types/SearchAlgoliaVolopHit';
import { searchApiFetchPromotedVolop } from '#/modules/search/api/searchApiFetchPromotedVolop';
import { SEARCH_REFINEMENT_NAMES } from '#/modules/search/constants/refinements/searchRefinementTypes';
import { extractLocationAnalyticsProperties } from '#/modules/search/helpers/analytics/extractLocationAnalyticsProperties';
import { radiusIntToString } from '#/modules/search/helpers/radius';
import { isLocationEmpty } from '#/modules/search/helpers/searchIsLocationEmpty';
import type { SearchFiltersByName } from '#/modules/search/types/FiltersByName/SearchFiltersByName';
import type { SearchFacetId } from '#/modules/search/types/SearchFacet';
import type { SearchFacetCounts } from '#/modules/search/types/SearchFacetCounts';
import type { SearchFiltersById } from '#/modules/search/types/SearchFiltersById';
import type { SearchLocation } from '#/modules/search/types/SearchLocation';
import type { SearchSortType } from '#/modules/search/types/SearchSortType';
import type { SearchType } from '#/modules/search/types/SearchType';
import {
  algoliaSearchMulti,
  type AlgoliaSearchMultiQuery,
} from '#/utils/algolia/algoliaSearchMulti';
import { algoliaBuildFilterString } from '#/utils/algolia/helpers/algoliaBuildFilterString';
import { trackEvent } from '#/utils/analytics/track/trackEvent';
import { EMPTY_SEARCH_LOCATION } from '#/utils/constants/general/emptySearchLocation';
import { SEARCH_RADIUSES } from '#/utils/constants/general/searchRadiuses';
import { setLocalStorageJsonItem } from '#/utils/localStorage';
import { getListingTypeFromPathname } from '#/utils/search/getListingTypeFromPathname';
import { createZustandContextStore } from '#/zustand-stores/utils/createZustandContextStore';

function parseRadius(radius: string, locale: I18nLocale) {
  const isValidRadius = SEARCH_RADIUSES[locale][radius];

  if (isValidRadius) return radius;
}

type SearchContextState = {
  query: string;
  pageIndex: number;
  resultsByPage: Partial<
    Record<
      string,
      SearchResponse<SearchAlgoliaHit> & Required<SearchPagination>
    >
  >;
  isSearching: boolean;
  sort: SearchSortType;
  facetCounts: SearchFacetCounts | undefined;
  filters: SearchFiltersById;
  filtersByName: SearchFiltersByName;
  searchLocation: SearchLocation | undefined;
  radius: string | undefined;
  promotedListing: {
    volopHit: SearchAlgoliaVolopHit;
    promotionType: ApiVolopPromotionType;
  } | null;
  orgVolopCountsByOrgId: Record<string, number> | undefined;
  // TODO(@paprikka): review naming + whether this can be simplified.
  volopListingTotal: number | undefined;
};

const emptyState: SearchContextState = {
  query: '',
  facetCounts: undefined,
  pageIndex: 0,
  resultsByPage: {},
  isSearching: false,
  sort: 'relevance',
  filters: { type: 'JOB' },
  filtersByName: { type: 'JOB' },
  searchLocation: undefined,
  radius: undefined,
  promotedListing: null,
  // TODO: remove or rename once we've dropped VOLOP_SEARCH_DEDUPLICATE_B_2026_04
  orgVolopCountsByOrgId: undefined,
  volopListingTotal: undefined,
};

const {
  ZustandContextProvider,
  useZustandContextStore,
  useZustandContextSelector,
} = createZustandContextStore<SearchContextState>({
  fallbackState: emptyState,
});

export function SearchContextProvider({ children }: { children: ReactNode }) {
  const { locale } = useI18n();
  const matches = useMatches();
  const location = useLocation();

  const searchLandingPageMatch = matches.find(isSearchLandingPageSSRMatch);
  const { embedSearchWidgetSearchLocation, embedSearchWidgetSearchRadius } =
    useEmbedSearchWidgetLayoutRouteLoaderData();

  const listingTypeFromPath = getListingTypeFromPathname(location.pathname);

  const initialState: SearchContextState = useMemo(() => {
    const baseEmptyState = {
      ...emptyState,
      filters: { type: listingTypeFromPath },
      filtersByName: { type: listingTypeFromPath },
    };

    if (embedSearchWidgetSearchLocation) {
      return {
        ...baseEmptyState,
        radius:
          radiusIntToString(
            embedSearchWidgetSearchRadius,
            embedSearchWidgetSearchLocation?.geoType,
          ) ?? '',
      };
    }

    if (!searchLandingPageMatch?.loaderData?.results) return baseEmptyState;

    const { content, results } = searchLandingPageMatch.loaderData;

    const pageIndex = results.page;

    return {
      query: content.searchText ?? '',
      pageIndex,
      facetCounts: undefined,
      resultsByPage: { [pageIndex]: results },
      isSearching: false,
      sort: 'relevance',
      filters: content.filters,
      filtersByName: content.filters,
      searchLocation: content.location.searchLocation,
      radius: content.location.radius
        ? parseRadius(content.location.radius.toString(), locale)
        : undefined,
      promotedListing: null,
      orgVolopCountsByOrgId: undefined,
      // Landing pages preload search results during SSR.
      // We need to pass the value to the client so that
      // when the VOLOP_SEARCH_DEDUPLICATE experiment is enabled
      // we don't lose access to it OR run a separate query.
      // Context: [sc-67439]
      volopListingTotal:
        content.filters.type === 'VOLOP' ? results.nbHits : undefined,
    };
  }, [
    locale,
    searchLandingPageMatch,
    embedSearchWidgetSearchLocation,
    embedSearchWidgetSearchRadius,
    listingTypeFromPath,
  ]);

  return (
    <ZustandContextProvider
      key={searchLandingPageMatch ? searchLandingPageMatch.pathname : undefined}
      initialState={initialState}
    >
      {children}
    </ZustandContextProvider>
  );
}

// Hooks

export function useSearchGetters() {
  const searchStore = useZustandContextStore();

  const getSearchFilterType = useCallback((): SearchType => {
    return searchStore.getState().filters.type;
  }, [searchStore]);

  const getSearchCurrentPageResults = useCallback(():
    | (SearchResponse<SearchAlgoliaHit> & Required<SearchPagination>)
    | undefined => {
    const { pageIndex, resultsByPage } = searchStore.getState();
    return resultsByPage[pageIndex];
  }, [searchStore]);

  const getSearchQuery = useCallback((): string => {
    return searchStore.getState().query;
  }, [searchStore]);

  const getSearchLocation = useCallback((): SearchLocation | undefined => {
    return searchStore.getState().searchLocation;
  }, [searchStore]);

  const getSearchFiltersById = useCallback((): SearchFiltersById => {
    return searchStore.getState().filters;
  }, [searchStore]);

  const getSearchFiltersByName = useCallback(() => {
    return searchStore.getState().filtersByName;
  }, [searchStore]);

  const getSearchSort = useCallback((): SearchSortType => {
    return searchStore.getState().sort;
  }, [searchStore]);

  const getSearchRadius = useCallback((): string => {
    const { radius } = searchStore.getState();
    return radius ?? '';
  }, [searchStore]);

  const getSearchPageIndex = useCallback((): number => {
    return searchStore.getState().pageIndex;
  }, [searchStore]);

  const selectedOption = useCallback(
    (facetId: SearchFacetId) => {
      const options = getSearchFiltersById()[facetId];
      return Array.isArray(options) ? options[0] : options;
    },
    [getSearchFiltersById],
  );

  const getSearchPromotedListing = useCallback((): {
    volopHit: SearchAlgoliaVolopHit;
    promotionType: ApiVolopPromotionType;
  } | null => {
    return searchStore.getState().promotedListing;
  }, [searchStore]);

  const getVolopListingTotal = useCallback((): number | undefined => {
    return searchStore.getState().volopListingTotal;
  }, [searchStore]);

  return {
    getSearchFilterType,
    getSearchCurrentPageResults,
    getSearchQuery,
    getSearchLocation,
    getSearchFiltersById,
    getSearchFiltersByName,
    getSearchSort,
    getSearchRadius,
    getSearchPageIndex,
    getSearchPromotedListing,
    getVolopListingTotal,
    selectedOption,
  };
}

export function useSearchFilterType(): SearchType {
  return useZustandContextSelector((state) => state.filters.type);
}

export function useSearchResultsByPage(): Partial<
  Record<number, SearchResponse<SearchAlgoliaHit> & Required<SearchPagination>>
> {
  return useZustandContextSelector((state) => state.resultsByPage);
}

export function useSearchCurrentPageResults<
  THitType extends SearchAlgoliaHit = SearchAlgoliaHit,
>(): (SearchResponse<THitType> & Required<SearchPagination>) | undefined {
  return useZustandContextSelector(
    (state) => state.resultsByPage[state.pageIndex],
  ) as (SearchResponse<THitType> & Required<SearchPagination>) | undefined;
}

export function useSearchQuery(): string {
  return useZustandContextSelector((state) => state.query);
}

export function useSearchLocation(): SearchLocation | undefined {
  return useZustandContextSelector((state) => state.searchLocation);
}

export function useSearchLocationOrEmpty(): SearchLocation {
  return useZustandContextSelector(
    (state) => state.searchLocation ?? EMPTY_SEARCH_LOCATION,
  );
}

export function useSearchFiltersById(): SearchFiltersById {
  return useZustandContextSelector((state) => state.filters);
}

export function useSearchFiltersByName() {
  return useZustandContextSelector((state) => state.filtersByName);
}

export function useSearchSort(): SearchSortType {
  return useZustandContextSelector((state) => state.sort);
}

export function useIsSearching(): boolean {
  return useZustandContextSelector((state) => state.isSearching);
}

export function useSearchRadius(): string {
  return useZustandContextSelector(({ radius }) => radius ?? '');
}

export function useSearchPageIndex(): number {
  return useZustandContextSelector((state) => state.pageIndex);
}

export function useSearchFacetCounts(): SearchFacetCounts | undefined {
  return useZustandContextSelector((state) => state.facetCounts);
}

const VOLOP_SEARCH_DEDUPLICATE_EXPERIMENT =
  'VOLOP_SEARCH_DEDUPLICATE_B_2026_04';

export function useIsVolopSearchDeduplicateEnabled(): boolean {
  const { environment } = useEnvironment();
  return (
    environment.abExperiments.some(
      (e) =>
        e.id === VOLOP_SEARCH_DEDUPLICATE_EXPERIMENT && e.bucket === 'ENABLED',
    ) === true
  );
}

export function useOrgVolopCountsByOrgId(): Record<string, number> | undefined {
  return useZustandContextSelector((state) => state.orgVolopCountsByOrgId);
}

export function useVolopListingTotal(): number | undefined {
  return useZustandContextSelector((state) => state.volopListingTotal);
}

const facetCountQueryOptions = {
  hitsPerPage: 0,
  page: 0,
  attributesToRetrieve: [],
  attributesToSnippet: [],
  attributesToHighlight: [],
};

function getFacetCountFiltersByName({
  filtersByName,
  hasEmbedSearchWidget,
}: {
  filtersByName: SearchFiltersByName;
  hasEmbedSearchWidget: boolean;
}): SearchFiltersByName {
  const facetCountFiltersByName = { ...filtersByName };

  if (!hasEmbedSearchWidget && facetCountFiltersByName.type === 'VOLOP') {
    facetCountFiltersByName.source = ['IDEALIST'];
  }

  return facetCountFiltersByName;
}

function getFacetCountCriteriaKey({
  filtersByName,
  query,
  radius,
  searchLocation,
}: {
  filtersByName: SearchFiltersByName;
  query: string;
  radius: string | undefined;
  searchLocation: SearchLocation | undefined;
}) {
  return JSON.stringify({
    filtersByName,
    query,
    radius,
    searchLocation,
  });
}

// New

let lastSearchRequestId = 0;
let lastFacetRequestId = 0;
export function useSearchActions() {
  const { i18n } = useI18n();
  const { appConfig } = useAppConfig();
  const isVolopSearchDeduplicateEnabled = useIsVolopSearchDeduplicateEnabled();

  const searchStore = useZustandContextStore();
  const {
    getSearchQuery,
    getSearchFilterType,
    getSearchLocation,
    getSearchFiltersById,
    getSearchFiltersByName,
    getSearchSort,
    getSearchRadius,
    getSearchPageIndex,
  } = useSearchGetters();

  // Check if we're in an embed widget context
  const { embedSearchWidget } = useEmbedSearchWidget();

  const isUsingAlgoliaMock = appConfig.algolia.mock && !embedSearchWidget;

  const updateSearchLocation = useCallback(
    (searchLocation: SearchLocation, didUserChange: boolean) => {
      if (didUserChange) {
        trackEvent(
          'Changed Search Location',
          extractLocationAnalyticsProperties(searchLocation),
        );
      }

      setLocalStorageJsonItem('searchLocation', searchLocation);
      searchStore.setState({
        searchLocation: {
          ...EMPTY_SEARCH_LOCATION,
          ...searchLocation,
          hasGeo: !isLocationEmpty(searchLocation),
        },
      });
    },
    [searchStore],
  );

  const setSearchRadius = useCallback(
    (radius: string | undefined) => {
      return searchStore.setState({ radius });
    },
    [searchStore],
  );

  const clearSearchLocation = useCallback(
    (didUserChange: boolean): void => {
      if (didUserChange) {
        trackEvent('Cleared Search Location');
      }

      setLocalStorageJsonItem('searchLocation', EMPTY_SEARCH_LOCATION);
      searchStore.setState({ searchLocation: EMPTY_SEARCH_LOCATION });
    },
    [searchStore],
  );

  const setSearchFilters = useCallback(
    (filtersById: SearchFiltersById) => {
      const { filters } = searchStore.getState();
      searchStore.setState({
        filters: filtersById,
        ...(filters.type !== filtersById.type && {
          facetCounts: undefined,
          orgVolopCountsByOrgId: undefined,
          volopListingTotal: undefined,
        }),
      });
    },
    [searchStore],
  );

  const setSearchFiltersByName = useCallback(
    (filtersByName: SearchFiltersByName) => {
      const { filtersByName: currentFiltersByName } = searchStore.getState();
      searchStore.setState({
        filtersByName,
        ...(currentFiltersByName.type !== filtersByName.type && {
          facetCounts: undefined,
          orgVolopCountsByOrgId: undefined,
          volopListingTotal: undefined,
        }),
      });
    },
    [searchStore],
  );

  const setSearchQuery = useCallback(
    (query: string) => {
      searchStore.setState({ query, pageIndex: 0 });
    },
    [searchStore],
  );

  const setSearchSort = useCallback(
    (sort: SearchSortType) => {
      searchStore.setState({ sort, pageIndex: 0 });
    },
    [searchStore],
  );

  const setSearchPageIndex = useCallback(
    (pageIndex: number) => {
      searchStore.setState({ pageIndex });
    },
    [searchStore],
  );

  const setSearchFacetCounts = useCallback(
    (facetCounts: SearchFacetCounts) => {
      searchStore.setState({ facetCounts });
    },
    [searchStore],
  );

  const fetchSearchPageResults = useCallback(
    async (pageIndex?: number, withFacetCounts = true) => {
      const filtersByName = getFacetCountFiltersByName({
        filtersByName: getSearchFiltersByName(),
        hasEmbedSearchWidget: Boolean(embedSearchWidget),
      });
      const filtersById = { ...getSearchFiltersById() };

      const facets = filtersByName
        ? SEARCH_REFINEMENT_NAMES[filtersByName.type]
        : [];

      if (!embedSearchWidget && filtersByName.type === 'VOLOP') {
        filtersById.volopSourceFacet = ['IDEALIST'];
      }

      searchStore.setState({ isSearching: true });

      lastSearchRequestId += 1;
      const searchRequestId = lastSearchRequestId;
      const facetRequestId = withFacetCounts ? lastFacetRequestId + 1 : null;
      if (facetRequestId !== null) lastFacetRequestId = facetRequestId;

      const searchLocation = getSearchLocation();
      const searchRadius = getSearchRadius();

      const searchQuery = getSearchQuery();
      const searchSort = getSearchSort();
      const searchPageIndex =
        typeof pageIndex === 'undefined' ? getSearchPageIndex() : pageIndex;
      const facetCountCriteriaKey = withFacetCounts
        ? getFacetCountCriteriaKey({
            filtersByName,
            query: searchQuery,
            radius: searchRadius,
            searchLocation,
          })
        : null;

      const facetCountIndexName = ['VOLOP', 'EVENT'].includes(
        filtersByName.type,
      )
        ? 'volopsIndexName'
        : 'siteIndexName';

      const { locationOptions } =
        searchLocation?.hasGeo &&
        searchLocation?.latitude &&
        searchLocation?.longitude
          ? searchAlgoliaOptionsLocationData(searchRadius, searchLocation)
          : {};

      const shouldFetchPromotedListing =
        !embedSearchWidget &&
        getSearchFilterType() === 'VOLOP' &&
        (!searchLocation?.countryCode || searchLocation.countryCode === 'US');

      const excludedAreasOfFocus =
        embedSearchWidget && getSearchFilterType() === 'VOLOP'
          ? embedSearchWidget.excludedAreasOfFocus
          : [];

      const config = getSearchAlgoliaDefaultConfig({ appConfig });

      const mainQueries: AlgoliaSearchMultiQuery[] = [
        {
          query: searchQuery,
          indexName: searchAlgoliaIndexSwitch({
            filtersById,
            sort: searchSort,
            appConfig,
          }),
          options: {
            ...searchAlgoliaOptionsWithSearchLocation({
              options: searchAlgoliaOptionsSwitch({
                i18n,
                filtersById,
                searchLocation,
                excludedAreasOfFocus,
                distinct: isVolopSearchDeduplicateEnabled,
              }),
              searchLocation,
              radius: searchRadius,
            }),
            page: searchPageIndex,
          },
        },
        // TODO(@paprikka): deduplicate with main query options after experiment
        ...(isVolopSearchDeduplicateEnabled && filtersById.type === 'VOLOP'
          ? [
              {
                query: searchQuery,
                indexName: appConfig.algolia.volopsIndexName,
                options: {
                  ...searchAlgoliaOptionsWithSearchLocation({
                    options: searchAlgoliaOptionsSwitch({
                      i18n,
                      filtersById,
                      searchLocation,
                      excludedAreasOfFocus,
                    }),
                    searchLocation,
                    radius: searchRadius,
                  }),
                  facets: ['orgID'],
                  hitsPerPage: 0,
                  page: 0,
                  attributesToRetrieve: [],
                  attributesToSnippet: [],
                  attributesToHighlight: [],
                  // 1000 is Algolia's max. This now only bounds the per-org link
                  // labels ("X more opportunities"); the header count reads this
                  // query's nbHits, so it is uncapped.
                  maxValuesPerFacet: 1000,
                },
                analytics: false,
                clickAnalytics: false,
                getRankingInfo: false,
              },
            ]
          : []),
      ];

      const facetCountQueries: AlgoliaSearchMultiQuery[] = withFacetCounts
        ? facets.map((facet) => ({
            query: searchQuery,
            options: {
              facets: [facet],
              filters: algoliaBuildFilterString({
                ...filtersByName,
                [facet]: undefined,
              }),
              ...locationOptions,
              ...facetCountQueryOptions,
            },
            indexName: appConfig.algolia[facetCountIndexName],
            analytics: false,
            clickAnalytics: false,
            getRankingInfo: false,
          }))
        : [];

      const [{ results }, promotedListing] = await Promise.all([
        algoliaSearchMulti<SearchAlgoliaHit>({
          isUsingAlgoliaMock: isUsingAlgoliaMock,
          config,
          queries: mainQueries,
        }),
        shouldFetchPromotedListing
          ? searchApiFetchPromotedVolop({
              latitude: searchLocation?.latitude,
              longitude: searchLocation?.longitude,
            })
          : null,
      ]).catch((e) => {
        if (lastSearchRequestId === searchRequestId) {
          searchStore.setState({ isSearching: false });
        }

        throw e;
      });

      const [searchResults, orgCountResult] = results || [];

      if (!searchResults) {
        throw new Error('Search results are missing from Algolia response.');
      }

      const orgVolopCountsByOrgId = orgCountResult?.facets?.orgID;
      // The org-count query runs without `distinct`, so its nbHits is the true,
      // uncapped VOLOP listing total (unlike the deduped main query's nbHits).
      const volopListingTotal = orgCountResult?.nbHits;

      // Check if a different search ran afterwards
      if (lastSearchRequestId === searchRequestId) {
        const { resultsByPage } = searchStore.getState();
        searchStore.setState({
          resultsByPage: {
            ...resultsByPage,
            [searchResults.page ?? 0]: searchResults,
          },
          promotedListing: promotedListing?.listingPromo ?? null,
          orgVolopCountsByOrgId,
          volopListingTotal,
          isSearching: false,
        });
      }

      if (
        facetCountQueries.length > 0 &&
        facetRequestId !== null &&
        lastFacetRequestId === facetRequestId &&
        facetCountCriteriaKey ===
          getFacetCountCriteriaKey({
            filtersByName: getFacetCountFiltersByName({
              filtersByName: getSearchFiltersByName(),
              hasEmbedSearchWidget: Boolean(embedSearchWidget),
            }),
            query: getSearchQuery(),
            radius: getSearchRadius(),
            searchLocation: getSearchLocation(),
          })
      ) {
        void algoliaSearchMulti<SearchAlgoliaHit>({
          isUsingAlgoliaMock: isUsingAlgoliaMock,
          config,
          queries: facetCountQueries,
        })
          .then(({ results: facetResults = [] }) => {
            if (
              lastFacetRequestId !== facetRequestId ||
              facetCountCriteriaKey !==
                getFacetCountCriteriaKey({
                  filtersByName: getFacetCountFiltersByName({
                    filtersByName: getSearchFiltersByName(),
                    hasEmbedSearchWidget: Boolean(embedSearchWidget),
                  }),
                  query: getSearchQuery(),
                  radius: getSearchRadius(),
                  searchLocation: getSearchLocation(),
                })
            ) {
              return null;
            }

            const facetCounts = facetResults.reduce(
              (
                acc: SearchFacetCounts,
                cur: SearchResponse<SearchAlgoliaHit>,
              ) => {
                Object.assign(acc, cur.facets);
                return acc;
              },
              {},
            );

            searchStore.setState({ facetCounts });
            return null;
          })
          .catch(() => {
            // algoliaSearchMulti already tracks request details. Keep the last
            // usable counts instead of disrupting displayed results.
          });
      }

      return searchResults;
    },
    [
      i18n,
      embedSearchWidget,
      getSearchFiltersById,
      getSearchFiltersByName,
      getSearchFilterType,
      getSearchLocation,
      getSearchPageIndex,
      getSearchQuery,
      getSearchRadius,
      getSearchSort,
      searchStore,
      isUsingAlgoliaMock,
      appConfig,
      isVolopSearchDeduplicateEnabled,
    ],
  );

  return {
    updateSearchLocation,
    setSearchRadius,
    clearSearchLocation,
    setSearchFilters,
    setSearchFiltersByName,
    setSearchQuery,
    setSearchSort,
    setSearchPageIndex,
    setSearchFacetCounts,
    fetchSearchPageResults,
  };
}
